/**
 * accountMemberSearch.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import * as React from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Membership } from "../models/auth";

import { authServiceFactory } from "../services/auth.service";

interface Props {
  className?: string,
  onMemberSelected(member: Membership): void
}

let timer: NodeJS.Timeout | null = null;

export function AccountMemberSearchComponent(props: Props): React.ReactElement<any, any> {
  const [loading, setLoading] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState<string>("");
  const [defaultMembers, setDefaultMembers] = React.useState<Array<Membership>>([]);
  const [members, setMembers] = React.useState<Array<Membership>>([]);
  //const [timer, setTimer] = React.useState<NodeJS.Timer | null>(null);

  React.useEffect(function () {
    authServiceFactory().getAccountMembers(undefined, 100)
      .then(mems => {
        mems.sort((a, b): number => {
          const res = a.lastname.localeCompare(b.lastname);
          if (res != 0) { return res; }
          return a.firstname.localeCompare(b.firstname);
        });
        setMembers(mems);
        setDefaultMembers(mems);
      }, err => {
        console.error("Error fetching account members:", err);
      });
  }, []);

  function searchValueChange(_event: React.SyntheticEvent, value: string) {
    if (!_event) { return; }
    setSearchValue(value);
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      try {
        setLoading(true);
        const mems = await authServiceFactory().getAccountMembers(value, 100);
        mems.sort((a, b): number => {
          const res = a.lastname.localeCompare(b.lastname);
          if (res != 0) { return res; }
          return a.firstname.localeCompare(b.firstname);
        });
        setMembers(mems);
      } catch (err) {
        console.error("Error fetching members:", err);
      } finally {
        setLoading(false);
        timer = null;
      }
    }, 1000);
  }

  function selectedOption(_event: React.SyntheticEvent, value: Membership | string | null) {
    if (!value) { return; }
    if (typeof value === "string") {
      console.log("string value:", value);
      return;
    }
    props.onMemberSelected(value);
    setSearchValue("");
    setMembers(defaultMembers);
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  return <>
    <Autocomplete id="account-member-search" className={props.className}
      loading={loading}
      options={members}
      fullWidth={true}
      size="small"
      value={null}
      inputValue={searchValue}
      onInputChange={searchValueChange}
      getOptionLabel={(member: Membership) => `${member.firstname} ${member.lastname}`}
      onChange={selectedOption}
      renderInput={(params) =>
        <TextField variant="outlined" {...params} label="Find Members" />} />
  </>;
}
